import type { QuestionSchema } from '../types/templateFormFields';
import { parseMultiSelectValue } from './intakeFieldFormatting';

export function matchesConditionalValue(
    parentValue: string | string[] | null | undefined,
    conditionValue: string | number | undefined,
    parentType?: string,
): boolean {
    if (parentType === 'multiselect') {
        return parseMultiSelectValue(parentValue).includes(String(conditionValue));
    }
    return parentValue === conditionValue;
}

export interface VisibilityQuestion {
    isVisibleToUser?: boolean | null;
    questionSchema: Pick<
        QuestionSchema,
        'name' | 'type' | 'isConditionalQuestion' | 'conditionedBy' | 'conditionValue'
    >;
}

/**
 * Whether a question is shown, given its siblings (the questions it may be conditioned on)
 * and a lookup for sibling answers. Applies the same rules to top-level questions within a
 * step and to nested questions within a contact block entry.
 */
export function isQuestionVisible<T extends VisibilityQuestion>(
    question: T,
    siblings: readonly T[],
    getValue: (sibling: T) => string | null | undefined,
): boolean {
    if (question.questionSchema.isConditionalQuestion) {
        const conditioningQuestion = siblings.find(
            (sibling) => sibling.questionSchema.name === question.questionSchema.conditionedBy,
        );
        if (!conditioningQuestion || !isMarkedVisible(conditioningQuestion)) return false;
        return matchesConditionalValue(
            getValue(conditioningQuestion),
            question.questionSchema.conditionValue,
            conditioningQuestion.questionSchema.type,
        );
    }

    return isMarkedVisible(question);
}

/**
 * False only for a conditional question whose condition isn't met. Unlike `isQuestionVisible`,
 * questions without a condition always pass, whatever their `isVisibleToUser`.
 */
export function isConditionMet<T extends VisibilityQuestion>(
    question: T,
    siblings: readonly T[],
    getValue: (sibling: T) => string | null | undefined,
): boolean {
    return (
        !question.questionSchema.isConditionalQuestion ||
        isQuestionVisible(question, siblings, getValue)
    );
}

/** Nested block questions may omit `isVisibleToUser`; only an explicit false/null hides them. */
function isMarkedVisible(question: VisibilityQuestion): boolean {
    return question.isVisibleToUser !== false && question.isVisibleToUser !== null;
}

export function getVisibleQuestions<T extends VisibilityQuestion>(
    questions: readonly T[],
    getValue: (sibling: T) => string | null | undefined,
): T[] {
    return questions.filter((question) => isQuestionVisible(question, questions, getValue));
}
